import React, { useState, useEffect, useRef, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import { motion, AnimatePresence } from 'framer-motion';

const bg = `${EASYCOMMERCE.assets}admin/img/icons/bg.png`;

const AI = ({
	isAPIKeyVisible,
	formValues,
	setFormValues,
	setIsAPIKeyVisible,
	showToast,
}) => {
	const [countDownTimer, setCountDownTimer] = useState(60);

	const intervalRef = useRef(null);

	const startCountdown = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (countDownTimer <= 0 || !isAPIKeyVisible) return;

		intervalRef.current = setInterval(() => {
			setCountDownTimer((prev) => {
				if (prev <= 1) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [isAPIKeyVisible, countDownTimer]);

	useEffect(() => {
		if (isAPIKeyVisible && countDownTimer > 0) {
			startCountdown();
		}
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isAPIKeyVisible, startCountdown]);

	const sendAPIKey = () => {
		easycommerce_modal(true);

		fetch(`${EASYCOMMERCE.rest_base}/connectivity/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': EASYCOMMERCE.nonce,
			},
			body: JSON.stringify({
				name: formValues.name,
				email: formValues.email,
				source: EASYCOMMERCE.home_url,
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				easycommerce_modal(false);

				if (data.success) {
					setCountDownTimer(60);
					setIsAPIKeyVisible(true);
					showToast('success', 'API Key Sent to your email');
					startCountdown();
				} else {
					showToast('error', 'Failed to send API key');
				}
			})
			.catch(() => {
				easycommerce_modal(false);
				showToast('error', 'Something went wrong');
			});
	};

	return (
		<div className="bg-cover bg-center w-full flex flex-col items-center">
			<div className="mb-8 font-inter">
				<h2 className="text-2xl text-center  text-ec-title font-medium">
					{__('AI Connectivity (Optional)', 'easycommerce')}
				</h2>
				<p
					className="text-[#606060] text-[16px] text-center mt-2 max-w-xl"
					dangerouslySetInnerHTML={{
						__html: __(
							'EasyCommerce includes AI tools like <strong>AI Analyst</strong>, <strong>AI Writer</strong>, and <strong>AI Image Editor</strong>. Would you like to enable them now? You can turn AI on anytime later.',
							'easycommerce'
						),
					}}
				/>
			</div>

			<div className="flex items-center gap-1 font-inter mb-4">
				<input
					type="checkbox"
					name="connectAI"
					id="connectAI"
					className="easycommerce-checkbox-input"
					checked={formValues.connectAI || false}
					onChange={(e) =>
						setFormValues((prev) => ({ ...prev, connectAI: e.target.checked }))
					}
				/>
				<label htmlFor="connectAI" className="text-[#828291] text-[16px]">
					{__('Yes, connect my store to EasyCommerce AI now', 'easycommerce')}
				</label>
			</div>

			{formValues.connectAI && (
				<AnimatePresence>
					<motion.div
						className="w-full"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -20 }}
						transition={{ duration: 0.3 }}
					>
						<div className="flex flex-col space-y-2 mb-6">
							<label className="text-ec-body text-[16px] font-medium">
								Your Name
							</label>
							<input
								type="text"
								name="easysuite-api-name"
								className="easycommerce-wizard-input w-full"
								placeholder="Enter your name"
								value={formValues.name || ''}
								onChange={(e) =>
									setFormValues((prev) => ({ ...prev, name: e.target.value }))
								}
							/>

							<p className="text-[#606060] text-[14px] mt-1">
								{__(
									'Add your name to receive more relevant and personalized AI output.',
									'easycommerce'
								)}
							</p>
						</div>

						<div className="flex flex-col space-y-2 mb-6">
							<label className="text-ec-body text-[16px] font-medium">
								Your Email
							</label>

							<div className="flex gap-2">
								<input
									type="email"
									name="easysuite-api-email"
									className="easycommerce-wizard-input grow"
									placeholder="Enter your email"
									value={formValues.email || ''}
									onChange={(e) =>
										setFormValues((prev) => ({
											...prev,
											email: e.target.value,
										}))
									}
								/>

								<button
									className="w-[130px] h-12 font-inter bg-ec-primary group border border-ec-primary rounded-lg text-white font-normal hover:text-white hover:bg-ec-secondary focus:shadow-none lg:text-base md:text-xs sm:text-sm transition-all ease-in-out duration-300 leading-[26px] disabled:opacity-50 disabled:cursor-not-allowed"
									onClick={(e) => {
										e.preventDefault();
										sendAPIKey();
									}}
									disabled={countDownTimer > 0 && isAPIKeyVisible}
								>
									{__('Send My Key', 'easycommerce')}
								</button>
							</div>

							<p className="text-[#606060] text-[14px] mt-1">
								{isAPIKeyVisible
									? __(
											`API key sent! Please check your email. You can request again ${
												countDownTimer > 0
													? 'in ' + countDownTimer + ' seconds'
													: 'now'
											}.`,
											'easycommerce'
									  )
									: __(
											'Enter a valid email address so we can send you your free API key to connect to AI.',
											'easycommerce'
									  )}
							</p>
						</div>

						{isAPIKeyVisible && (
							<div className="flex flex-col space-y-2 mb-6">
								<label className="text-ec-body text-[16px] font-medium">
									API Key
								</label>
								<input
									type="text"
									name="easysuite-api-apiKey"
									className="easycommerce-wizard-input w-full"
									placeholder="Enter your API key"
									onChange={(e) =>
										setFormValues((prev) => ({
											...prev,
											apiKey: e.target.value,
										}))
									}
								/>

								<p className="text-[#606060] text-[14px] mt-1">
									{__(
										'Input the API key we sent to you, and click the "Verify" button below.',
										'easycommerce'
									)}
								</p>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			)}
		</div>
	);
};

export default AI;
